/**
 * ColumnControls — wspólne kontrolki zwijanych + poszerzalnych kolumn buildera menu.
 *
 * `ColumnResizer` — pionowy uchwyt do ręcznego poszerzania sąsiedniej kolumny (drag).
 * `CollapsedColumnRail` — wąski pasek zwiniętej kolumny (ikona rozwijania u góry +
 * pionowa etykieta).
 *
 * Używane na wielu powierzchniach (Kreator menu tenanta/admina, hub — edytor szablonu),
 * żeby zwijanie/poszerzanie działało wszędzie identycznie i bez duplikacji kodu.
 */
import { PanelLeftOpen } from 'lucide-react';

export function ColumnResizer({
  width,
  onWidthChange,
  min,
  max,
}: {
  width: number;
  onWidthChange: (w: number) => void;
  min: number;
  max: number;
}) {
  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev: PointerEvent) => {
      onWidthChange(Math.min(max, Math.max(min, startW + (ev.clientX - startX))));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      onPointerDown={onPointerDown}
      className="group relative flex w-3 shrink-0 cursor-col-resize items-center justify-center"
      data-testid="menu-builder-column-resizer"
      title="Przeciągnij, aby zmienić szerokość"
    >
      {/* Widoczny wskaźnik uchwytu = „grip pill": krótka zaokrąglona belka na środku wysokości
          (zamiast kreski na całą wysokość, decyzja właścicielki 2026-09-23); przy najechaniu
          wydłuża się i akcentuje. */}
      <span
        aria-hidden="true"
        className="h-8 w-1 rounded-full bg-border-primary transition-all group-hover:h-12 group-hover:bg-accent-400"
      />
    </div>
  );
}

export function CollapsedColumnRail({
  label,
  ariaLabel,
  onExpand,
  testId,
}: {
  label: string;
  ariaLabel: string;
  onExpand: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onExpand}
      aria-label={ariaLabel}
      title={ariaLabel}
      className="flex w-9 shrink-0 flex-col items-center gap-2 border-r border-border-secondary bg-bg-elevated pt-3 text-text-muted transition hover:bg-bg-secondary hover:text-text-primary"
      data-testid={testId}
    >
      <PanelLeftOpen className="h-4 w-4" />
      <span className="text-[10px] font-semibold uppercase tracking-wider [writing-mode:vertical-rl]">
        {label}
      </span>
    </button>
  );
}
